import styled from '@emotion/styled';
import type { FunctionComponent } from 'react';

import type { FeedbackType } from './feedback-dialog-store';
import { ReportSkillIcon } from './icons/ReportSkillIcon';
import { RequestChangeIcon } from './icons/RequestChangeIcon';
import { RequestNewIcon } from './icons/RequestNewIcon';
import { useFeedbackDialogStore } from './use-feedback-dialog-store';

type TabsSwitcherProps = {
    readonly activeTab: FeedbackType;
    readonly onTabChange: (tab: FeedbackType) => void;
};

type ActiveProps = {
    readonly isActive: boolean;
};

const TabsContainer = styled.div({
    background: '#f6f6f7',
    borderRadius: '0.75rem',
    display: 'flex',
    padding: '0.25rem',
    width: '100%',
});

const TabButton = styled.div<ActiveProps>(({ isActive = false }) => ({
    alignItems: 'center',
    background: isActive ? '#FBFBFF' : 'transparent',
    borderRadius: '0.5rem',
    boxShadow: isActive ? '-0.02rem -0.02rem 0px rgba(0, 0, 0, 0.05)' : 'none',
    cursor: 'pointer',
    display: 'flex',
    flex: 1,
    gap: '0.5rem',
    justifyContent: 'center',
    padding: '0.25rem 0.375rem',
}));

const TabIcon = styled.div({
    height: '1rem',
    position: 'relative',
    width: '1rem',
});

const TabText = styled.span<ActiveProps>(({ isActive = false }) => ({
    color: isActive ? '#040F16' : '#6E6E6C',
    fontFamily: 'FT Regola Neue',
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: '1.25rem',
}));

export const TabsSwitcher: FunctionComponent<TabsSwitcherProps> = ({ activeTab, onTabChange }) => {
    const reportSkillLabel = useFeedbackDialogStore((state) => state.reportSkillLabel);
    const requestNewSkillLabel = useFeedbackDialogStore((state) => state.requestNewSkillLabel);
    const reportContentIssueLabel = useFeedbackDialogStore((state) => state.reportContentIssueLabel);

    return (
        <TabsContainer>
            <TabButton isActive={activeTab === 'request'} onClick={() => onTabChange('request')}>
                <TabIcon>
                    <RequestNewIcon />
                </TabIcon>
                <TabText isActive={activeTab === 'request'}>{requestNewSkillLabel}</TabText>
            </TabButton>
            <TabButton isActive={activeTab === 'report'} onClick={() => onTabChange('report')}>
                <TabIcon>
                    <ReportSkillIcon />
                </TabIcon>
                <TabText isActive={activeTab === 'report'}>{reportSkillLabel}</TabText>
            </TabButton>
            <TabButton isActive={activeTab === 'change'} onClick={() => onTabChange('change')}>
                <TabIcon>
                    <RequestChangeIcon />
                </TabIcon>
                <TabText isActive={activeTab === 'change'}>{reportContentIssueLabel}</TabText>
            </TabButton>
        </TabsContainer>
    );
};
